import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import {
  addMonths,
  addWeeks,
  eachDayOfInterval,
  endOfDay,
  endOfToday,
  format,
  getDate,
  isFuture,
  isPast,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns"
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Format } from "shared-ts/classes/timestamp"
import { DisabledModeT } from "shared-ts/hooks/useFilter/types/filter.types"
import { CheckboxLabelled } from "shared-web/components/base/control/CheckboxLabelled"
import { Card } from "shared-web/components/base/form/Card"
import { TextDateInput } from "shared-web/components/base/input/text/TextDateInput"
import {
  popoverPanelAnchor,
  popoverPanelAnchorEnd,
} from "shared-web/components/base/overlay/Popover/popoverStyles"
import { Icon, IconSizes } from "shared-web/components/Icon"

import { classNames } from "lib/classNames"

export interface DateRange {
  start: Date
  end: Date
}

type DatePickerProps = {
  buttonClassName?: string
  fixedLabel?: string
  disabled?: boolean
  disabledDaysMode?: DisabledModeT
  allowedDateRange?: DateRange
  showInvalidBorder?: boolean
  panelAlignment?: "left" | "right"
  renderAboveModal?: boolean
  placeholder?: string
  dateFormat?: Format
} & (
  | {
      pickedDate: Date | null
      onPickDate: (date: Date) => void
      showASAPCheckbox?: false
    }
  | {
      pickedDate: Date | "ASAP" | null
      onPickDate: (date: Date | "ASAP") => void
      showASAPCheckbox: true
    }
)

export function DatePicker(props: DatePickerProps) {
  const [date, setDate] = useState(endOfToday())

  const {
    disabledDaysMode,
    placeholder = "Select date",
    dateFormat = Format.DATE,
  } = props

  const start = startOfWeek(startOfMonth(date))
  const end = subDays(addWeeks(start, 6), 1)

  const days = eachDayOfInterval({
    start,
    end,
  })
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null)

  function incrementMonth() {
    setDate(addMonths(date, 1))
  }

  function decrementMonth() {
    setDate(subMonths(date, 1))
  }

  useEffect(() => {
    if (!props.pickedDate || props.pickedDate === "ASAP") {
      setDate(endOfToday())
      return
    }

    setDate(props.pickedDate)
  }, [props.pickedDate])

  const popoverButtonRef = useRef<HTMLButtonElement>(null)

  function updateButtonRect() {
    if (popoverButtonRef.current) {
      setButtonRect(popoverButtonRef.current.getBoundingClientRect())
    }
  }

  let label = ""
  switch (true) {
    case !!props.fixedLabel:
      label = props.fixedLabel
      break
    case !props.pickedDate: {
      label = placeholder
      break
    }
    case props.pickedDate === "ASAP": {
      label = "ASAP"
      break
    }
    case isToday(props.pickedDate as Date): {
      label = "Today"
      break
    }
    default: {
      label = format(props.pickedDate as Date, dateFormat)
    }
  }

  const renderCalendarContent = (close: () => void) => (
    <Card className="min-w-2xs z-30 flex flex-col gap-y-4 shadow-lg ring-1 ring-black/5">
      <TextDateInput
        date={props.pickedDate === "ASAP" ? null : props.pickedDate}
        onChangeDate={(d) => {
          if (!d) {
            return
          }

          props.onPickDate(d)
          close()
          popoverButtonRef.current?.blur()
        }}
        defaultRefDate={endOfToday()}
        disabledMode={disabledDaysMode}
        containerClassName="mb-2"
        placeholder="Date"
        autoFocus
      />
      <div className="text-default flex flex-row justify-between">
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault()
            decrementMonth()
          }}
          data-testid="date-picker-previous-month-button"
        >
          <Icon icon={ChevronLeft} size={IconSizes.Medium} />
        </button>
        <div className="text-xs font-bold tracking-wide">
          {format(date, "MMMM yyyy")}
        </div>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault()
            incrementMonth()
          }}
          data-testid="date-picker-next-month-button"
        >
          <Icon icon={ChevronRight} size={IconSizes.Medium} />
        </button>
      </div>
      <div className="gap-x- grid grid-cols-7 gap-x-6 gap-y-2">
        {daysOfWeek.map(({ id, abbreviation }) => (
          <div
            key={id}
            className="text-default select-none text-center text-sm font-semibold"
          >
            {abbreviation}
          </div>
        ))}

        {days.map((day) => {
          let disabled = false

          if (disabledDaysMode === "future") {
            disabled = isFuture(day)
          }

          if (disabledDaysMode === "past") {
            disabled = isPast(day) && !isToday(day)
          }

          if (props.allowedDateRange) {
            disabled =
              disabled || !isWithinInterval(day, props.allowedDateRange)
          }

          const isFutureDayOutsideMonth =
            isFuture(day) && !isSameMonth(date, day)

          const isPickedDay =
            !!props.pickedDate &&
            props.pickedDate !== "ASAP" &&
            isSameDay(props.pickedDate, day)

          return (
            <button
              type="button"
              key={day.toDateString()}
              className={classNames(
                "select-none text-center text-sm",
                isFutureDayOutsideMonth
                  ? "text-gray-600 dark:text-gray-200"
                  : isPickedDay
                    ? "text-cobalt-400 dark:text-cobalt-300 font-semibold"
                    : "text-black disabled:text-gray-500 dark:text-white dark:disabled:text-gray-300"
              )}
              disabled={disabled}
              onMouseDown={(e) => {
                e.preventDefault()
                props.onPickDate(day)
                close()
                popoverButtonRef.current?.blur()
              }}
            >
              {getDate(day)}
            </button>
          )
        })}
      </div>
      {props.showASAPCheckbox && (
        <CheckboxLabelled
          className="text-xs"
          checked={!!props.pickedDate && props.pickedDate === "ASAP"}
          onChange={() => {
            if (props.pickedDate !== "ASAP") {
              props.onPickDate("ASAP")
              close()
              popoverButtonRef.current?.blur()
            } else {
              props.onPickDate(endOfDay(new Date()))
            }
          }}
        >
          ASAP
        </CheckboxLabelled>
      )}
    </Card>
  )

  return (
    <Popover className="relative">
      <PopoverButton
        className={classNames(
          "bg-input-default border-v2-border-default shadow-control group flex h-10 w-full items-center gap-x-2 rounded border px-3",
          "hover:not-disabled:border-v2-border-strong",
          "disabled:border-v2-border-default-disabled disabled:bg-input-disabled disabled:color-disabled",
          props.showInvalidBorder &&
            "border-v2-border-danger hover:not-disabled:border-v2-border-danger",
          props.buttonClassName
        )}
        ref={popoverButtonRef}
        onClick={updateButtonRect}
        data-testid="picked-date"
        disabled={props.disabled}
      >
        <Icon
          icon={Calendar}
          size={IconSizes.ExtraSmall}
          className="color-icon-subtle group-disabled:color-icon-disabled"
        />
        <span
          className={classNames(
            "text-body group-disabled:color-disabled flex-1 truncate text-left",
            !props.pickedDate ? "color-subtler" : "color-default"
          )}
        >
          {label}
        </span>
      </PopoverButton>

      <PopoverPanel
        anchor={
          props.renderAboveModal
            ? undefined
            : props.panelAlignment === "right"
              ? popoverPanelAnchorEnd
              : popoverPanelAnchor
        }
        className={props.renderAboveModal ? undefined : "z-[60] w-fit"}
      >
        {({ close }) =>
          props.renderAboveModal
            ? createPortal(
                <div
                  style={{
                    position: "absolute",
                    top: `${buttonRect ? buttonRect.bottom + window.scrollY + 4 : 0}px`,
                    left: `${buttonRect ? buttonRect.left + window.scrollX : 0}px`,
                    zIndex: 9999,
                  }}
                >
                  {renderCalendarContent(close)}
                </div>,
                document.body
              )
            : renderCalendarContent(close)
        }
      </PopoverPanel>
    </Popover>
  )
}

const daysOfWeek = [
  { id: "sunday", abbreviation: "S" },
  { id: "monday", abbreviation: "M" },
  { id: "tuesday", abbreviation: "T" },
  { id: "wednesday", abbreviation: "W" },
  { id: "thursday", abbreviation: "T" },
  { id: "friday", abbreviation: "F" },
  { id: "saturday", abbreviation: "S" },
]
